import { api } from '@goaltree/backend/convex/_generated/api'
import { fitPlan, planRequest } from '@goaltree/backend/convex/lib/onboardingFlow'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@goaltree/ui/components/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@goaltree/ui/components/dropdown-menu'
import { cn } from '@goaltree/ui/lib/utils'
import {
  createFileRoute,
  redirect,
  useBlocker,
  useNavigate,
  useRouter,
} from '@tanstack/react-router'
import { Authenticated, Unauthenticated, useConvexAuth, useQuery } from 'convex/react'
import {
  GitBranch,
  Globe,
  LayoutList,
  Link2,
  Loader2,
  MoreHorizontal,
  RotateCcw,
} from 'lucide-react'
import { usePostHog } from 'posthog-js/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import GoalBuildLoader from '@/components/goal-build-loader'
import HierarchyFlow from '@/components/hierarchy-flow'
import HierarchyTree from '@/components/hierarchy-tree'
import { MobileScreenHeader } from '@/components/mobile-screen-header'
import { GoalFlowSteps, OnboardingHeader } from '@/components/onboarding-progress'
import { PlanHabits } from '@/components/plan-habits'
import { PlanReview } from '@/components/plan-review'
import { type CritiqueKey, SecondOpinion } from '@/components/second-opinion'
import SignUpGate from '@/components/sign-up-gate'
import { SiteHeader } from '@/components/site-header'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { useOnboarding } from '@/hooks/use-onboarding'
import { useOnboardingMode } from '@/hooks/use-onboarding-mode'
import { markStartHere } from '@/lib/first-run'
import { planGoal } from '@/lib/plan-goal'
import { goalSlug } from '@/lib/utils'
import { useHeaderStore } from '@/store/header.store'
import { useOnboardingStore } from '@/store/onboarding.store'

export const Route = createFileRoute('/goals/new/preview')({
  head: () => ({
    meta: [{ title: 'Your plan — Goaltree' }],
  }),
  beforeLoad: () => {
    if (!useOnboardingStore.getState().generatedTree) {
      throw redirect({ to: '/goals/new', search: { goal: undefined } })
    }
  },
  component: PreviewComponent,
})

type View = 'list' | 'flow'

function PreviewComponent() {
  const { t, i18n } = useTranslation()
  const posthog = usePostHog()
  const navigate = useNavigate()
  const store = useOnboardingStore()
  const { saveTree, decompose, isLoading, goalLimitReached, streamKey } = useOnboarding()
  const tree = store.generatedTree!
  const setHeaderScrolled = useHeaderStore((s) => s.setScrolled)
  const [view, setView] = useState<View>('list')
  const isPublic = useOnboardingStore((s) => s.isPublic)
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [discardOpen, setDiscardOpen] = useState(false)
  const { isAuthenticated } = useConvexAuth()
  const router = useRouter()
  const autoSaveAfterAuth = useOnboardingStore((s) => s.autoSaveAfterAuth)
  const discardConfirmedRef = useRef(false)
  const onboarding = useOnboardingMode()
  const bottomNav = isAuthenticated && !onboarding.active
  const splitRun = useOnboardingStore((s) => s.splitRun)
  const [planningNext, setPlanningNext] = useState<string | null>(null)
  // Captured at the first save of a split run: later saves see goals exist.
  const firstRunRef = useRef<boolean | null>(null)
  const sourceText = splitRun ? tree.title : store.goalText

  async function handleSecondOpinion(critique: CritiqueKey[], note: string) {
    const { goalText, ...inputs } = planRequest(sourceText, store.planAnswers, {
      locale: i18n.language,
    })
    const built = await decompose(
      goalText,
      {
        ...inputs,
        dueDate: tree.dueDate,
        ...(store.ownTasks.length > 0 && { seedTasks: store.ownTasks }),
      },
      { priorTree: tree, critique, note },
    )
    const revised =
      built && fitPlan(built, sourceText, store.planAnswers, { locale: i18n.language })
    if (revised) {
      store.setGeneratedTree(store.category ? { ...revised, category: store.category } : revised)
      posthog.capture('second_opinion', { critique, category: tree.category })
      toast.success(t('secondOpinion.updated'))
    }
  }

  // Decided before saving: the save itself marks onboarding complete.
  const prefs = useQuery(api.preferences.getMyPreferences, isAuthenticated ? {} : 'skip')
  const goals = useQuery(api.nodes.getUserGoals, isAuthenticated ? {} : 'skip')
  const ready = prefs !== undefined && goals !== undefined
  const firstRun =
    ready &&
    !(prefs as { onboardingCompletedAt?: number }).onboardingCompletedAt &&
    !goals.some((g) => !g.isInbox)

  // Suggested habits the user unticked; dropped from the tree on save.
  const [habitsOff, setHabitsOff] = useState<Set<number>>(() => new Set())

  async function handleSave() {
    const { templateId } = useOnboardingStore.getState()
    const toSave = tree.habits
      ? { ...tree, habits: tree.habits.filter((_, i) => !habitsOff.has(i)) }
      : tree
    const goalId = await saveTree(toSave, isPublic, templateId)
    if (goalId) {
      posthog.capture('goal_confirmed', { category: tree.category })
      const wasFirstRun = (firstRunRef.current ??= firstRun || onboarding.active)
      const run = useOnboardingStore.getState().splitRun
      if (run?.queue.length) {
        const [next, ...queue] = run.queue
        setHabitsOff(new Set())
        setPlanningNext(next)
        const nextTree = await planGoal(decompose, next, store.planAnswers, i18n.language, {
          fullText: run.source,
          otherGoals: run.titles.filter((x) => x !== next),
          part: run.parts[run.titles.indexOf(next)],
          seed: false,
        })
        setPlanningNext(null)
        if (nextTree) {
          store.setSplitRun({ ...run, queue, index: run.index + 1 })
          store.setGeneratedTree(nextTree)
          return
        }
        // Hand the unplanned goal back to the questions step to retry.
        discardConfirmedRef.current = true
        store.setSplitRun(null)
        store.setGeneratedTree(null)
        store.setGoalText(next)
        toast.error(t('planner.split.nextFailed'))
        navigate({ to: '/goals/new/questions' })
        return
      }
      discardConfirmedRef.current = true
      // onboarding.active is sticky for this mount, so it still holds when the
      // prefs snapshot was stale or onboarding was completed by the save itself.
      if (wasFirstRun) {
        navigate({ to: '/onboarding/user-profile', search: { goalId } })
        return
      }
      // Land on the new goal itself (a one-time banner there points to Today).
      markStartHere(goalId)
      store.reset()
      if (run && run.total > 1) navigate({ to: '/goals' })
      else navigate({ to: '/goals/$goalId', params: { goalId: goalSlug(tree.title, goalId) } })
    }
  }

  useEffect(() => {
    posthog.capture('goal_decomposition_completed', { category: tree.category })
  }, [])

  // After SignUpGate auth, the parent layout remounts this component as authenticated.
  // autoSaveAfterAuth in the store (survives remount and the OAuth/magic-link
  // round-trip) signals we should save immediately — claimed so it saves once.
  useEffect(() => {
    if (
      isAuthenticated &&
      ready &&
      autoSaveAfterAuth &&
      useOnboardingStore.getState().claimAutoSave()
    )
      handleSave()
  }, [isAuthenticated, ready, autoSaveAfterAuth])

  const { proceed, reset, status } = useBlocker({
    shouldBlockFn: () => !isLoading && !discardConfirmedRef.current,
    enableBeforeUnload: true,
    withResolver: true,
  })

  useEffect(() => {
    if (status === 'blocked') setDiscardOpen(true)
    else setDiscardOpen(false)
  }, [status])

  // Discard from the confirm dialog (triggered by an intercepted navigation, e.g.
  // clicking a nav link) — continue to wherever the user was actually going.
  function handleConfirmDiscard() {
    setDiscardOpen(false)
    store.reset()
    proceed?.()
  }

  // "Start over" button — discard and go back to the new-goal input.
  function handleDiscard() {
    discardConfirmedRef.current = true
    setDiscardOpen(false)
    if (status === 'blocked') reset()
    navigate({ to: '/goals/new', search: { goal: undefined } })
    store.reset()
  }

  function handleClose() {
    // Explicit close — bypass the unsaved-changes blocker.
    discardConfirmedRef.current = true
    if (status === 'blocked') reset()
    if (router.history.length > 1) router.history.back()
    else navigate({ to: isAuthenticated ? '/goals' : '/' })
    store.reset()
  }

  // Back to the questions: keeps the tree; changing an answer rebuilds it. An
  // adapted template goes back to its page instead.
  function handleBack() {
    discardConfirmedRef.current = true
    if (status === 'blocked') reset()
    const { templateId } = useOnboardingStore.getState()
    if (templateId) navigate({ to: '/templates/$templateId', params: { templateId } })
    else navigate({ to: '/goals/new/questions' })
  }

  function handleEditGoal() {
    discardConfirmedRef.current = true
    if (status === 'blocked') reset()
    navigate({ to: '/goals/new', search: { goal: store.goalText } })
  }

  function handleKeep() {
    setDiscardOpen(false)
    if (status === 'blocked') reset()
  }

  // Secondary actions live behind ⋯ so the footer keeps one clear call to action.
  const planMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t('planPreview.more')}
        title={t('planPreview.more')}
        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <MoreHorizontal className="h-5 w-5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuCheckboxItem
          checked={isPublic}
          onCheckedChange={(v) => store.setIsPublic(!!v)}
          className="cursor-pointer gap-2 px-3 text-sm"
        >
          <Globe className="h-4 w-4" />
          {t('planner.isPublic')}
        </DropdownMenuCheckboxItem>
        <DropdownMenuItem
          className="cursor-pointer gap-2 px-3 text-sm"
          onClick={() => {
            navigator.clipboard.writeText(
              `https://goaltree.ai/g?goal=${encodeURIComponent(tree.title)}`,
            )
            toast.success(t('planner.suggest.shareGoalCopied'))
          }}
        >
          <Link2 className="h-4 w-4" />
          {t('planner.shareIt')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="cursor-pointer gap-2 px-3 text-sm" onClick={handleDiscard}>
          <RotateCcw className="h-4 w-4" />
          {t('onboarding.startOver')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )

  // List/graph segmented button group — matches the inbox header switcher.
  const viewSwitcher = (
    <div className="flex items-center overflow-hidden rounded-md border border-border divide-x divide-border">
      {(
        [
          { value: 'list', icon: LayoutList, label: t('goals.viewList') },
          { value: 'flow', icon: GitBranch, label: t('goals.viewGraph') },
        ] as const
      ).map(({ value, icon: Icon, label }) => (
        <button
          key={value}
          type="button"
          aria-label={label}
          title={label}
          onClick={() => setView(value)}
          className={cn(
            'cursor-pointer flex h-8 w-8 items-center justify-center transition-colors',
            view === value
              ? 'bg-muted text-foreground'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          )}
        >
          <Icon className="h-3.5 w-3.5" />
        </button>
      ))}
    </div>
  )

  // Mobile header shows the list/graph group plus search; desktop keeps the
  // group in the site header.
  const rightSlot = (
    <div className="flex items-center gap-1">
      {viewSwitcher}
      {planMenu}
    </div>
  )

  // Store was cleared (discard/close) — avoid rendering with a null tree while
  // the pending navigation settles.
  if (!store.generatedTree) return null

  if (planningNext)
    return (
      <>
        {onboarding.active ? (
          <OnboardingHeader step={onboarding.step} />
        ) : (
          <MobileScreenHeader title={t('nav.newGoal')} showInbox={false} showSearch={false} />
        )}
        <GoalBuildLoader goal={planningNext} streamKey={streamKey} />
        {splitRun && (
          <p className="pb-6 text-center text-sm text-muted-foreground">
            {t('planner.split.planningNext', { n: splitRun.index + 1, total: splitRun.total })}
          </p>
        )}
      </>
    )

  const splitBanner = splitRun && splitRun.total > 1 && (
    <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-primary-text">
      {t('planner.split.progress', { n: splitRun.index, total: splitRun.total })}
    </p>
  )

  return (
    <>
      {onboarding.active ? (
        <OnboardingHeader
          step={onboarding.step}
          onBack={isLoading ? undefined : handleBack}
          rightSlot={rightSlot}
        />
      ) : (
        <>
          <MobileScreenHeader
            title={tree.title}
            showInbox={false}
            showSearch={false}
            rightSlot={rightSlot}
            onClose={handleClose}
          />
          <SiteHeader className="hidden lg:flex" rightSlot={rightSlot} />
        </>
      )}
      <UpgradeDialog
        open={goalLimitReached || upgradeOpen}
        onOpenChange={setUpgradeOpen}
        reason="goal-limit"
      />

      {view === 'flow' ? (
        <>
          <div className="flex min-h-0 flex-1 pt-(--app-hdr-h)">
            <HierarchyFlow tree={tree} />
          </div>
          <Authenticated>
            <div
              className={cn(
                'bg-background/80 px-4 pt-4 backdrop-blur-sm',
                bottomNav ? 'pb-20 lg:pb-6' : 'pb-6',
              )}
            >
              <div className="mx-auto max-w-2xl">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isLoading}
                  className="cursor-pointer group flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3.5 text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 hover:scale-[1.01] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50 disabled:scale-100"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('planner.saving')}
                    </>
                  ) : (
                    t('planPreview.start')
                  )}
                </button>
              </div>
            </div>
          </Authenticated>
          <Unauthenticated>
            <SignUpGate />
          </Unauthenticated>
        </>
      ) : (
        <PlanReview
          headerTop={
            <>
              {!onboarding.active && (
                <GoalFlowSteps
                  step={3}
                  onStep={(n) => (n === 1 ? handleEditGoal() : handleBack())}
                  className="mb-5"
                />
              )}
              {splitBanner}
            </>
          }
          offsetBottomNav={bottomNav}
          continueLabel={
            splitRun?.queue.length ? t('planner.split.saveNext') : t('planPreview.start')
          }
          onContinue={handleSave}
          continueLoading={isLoading}
          footerTop={
            <div className="mb-1 flex justify-center text-xs">
              <SecondOpinion
                onSubmit={handleSecondOpinion}
                loading={isLoading}
                className="cursor-pointer"
              />
            </div>
          }
          footerOverride={!isAuthenticated ? <SignUpGate onStartOver={handleDiscard} /> : undefined}
        >
          <HierarchyTree
            tree={tree}
            onUpdate={store.setGeneratedTree}
            onTitleHidden={setHeaderScrolled}
          />
          {tree.habits && (
            <PlanHabits
              habits={tree.habits}
              off={habitsOff}
              onToggle={(i) =>
                setHabitsOff((prev) => {
                  const next = new Set(prev)
                  if (next.has(i)) next.delete(i)
                  else next.add(i)
                  return next
                })
              }
            />
          )}
        </PlanReview>
      )}

      {/* Discard confirm dialog */}
      <AlertDialog
        open={discardOpen}
        onOpenChange={(open) => {
          if (!open) handleKeep()
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('planner.discardTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('planner.discardDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleKeep}>{t('planner.discardKeep')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDiscard}>
              {t('planner.discardConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
